/**
 * Module Switcher - Header popover for switching between modules.
 * Uses shared ModulePickerContent for the dropdown list.
 */

import React, { useState, useEffect } from 'react';
import { Grip } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGuardedNavigate } from '../../shared/hooks/useGuardedNavigate';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useModule } from '../../shared/contexts/ModuleContext';
import { useDirection } from '../../shared/contexts/DirectionContext';
import { ModuleConfig } from '../../shared/config/modules.config';
import { useModuleUsage } from '../../shared/hooks/useModuleUsage';
import { ModulePickerContent } from './ModulePickerContent';
import { useModulePickerDynamicItems } from './useModulePickerDynamicItems';

interface ModuleSwitcherProps {
  trigger?: React.ReactNode;
  align?: 'start' | 'end';
}

export function ModuleSwitcher({ trigger, align = 'end' }: ModuleSwitcherProps = {}) {
  const { t } = useTranslation('common');
  // The module picker's entries are `button`s calling navigate(), invisible to the
  // anchor interceptor in useUnsavedChangesWarning — same hole as the sidebar's.
  const navigate = useGuardedNavigate();
  const { isRtl } = useDirection();
  // Radix Popover doesn't always pick up document RTL — flip start/end manually so the
  // panel stays anchored to the trigger's near edge (and on-screen) in both directions.
  const resolvedAlign: 'start' | 'end' = isRtl
    ? (align === 'start' ? 'end' : 'start')
    : align;
  const { switchModule, pickerModules, disabledPickerModules, pinnedModules, isCurrentModule, isModuleEnabledForUser } = useModule();
  const { isFavorite, toggleFavorite, sortModules, recordModuleVisit, refresh } = useModuleUsage();
  const dynamicItems = useModulePickerDynamicItems();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Re-read localStorage when popover opens
  useEffect(() => {
    if (open) {
      refresh();
    } else {
      setSearchQuery('');
    }
  }, [open, refresh]);

  const handleModuleSelect = (module: ModuleConfig) => {
    if (isModuleEnabledForUser(module.id)) {
      recordModuleVisit(module.id);
      switchModule(module.id);
      setOpen(false);
      setSearchQuery('');
    }
  };

  const handlePageSelect = (path: string, module: ModuleConfig) => {
    if (isModuleEnabledForUser(module.id)) {
      recordModuleVisit(module.id);
      navigate(path);
      setOpen(false);
      setSearchQuery('');
    }
  };

  const handleHomeSelect = () => {
    navigate('/');
    setOpen(false);
    setSearchQuery('');
  };

  const handleToggleFavorite = (moduleId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavorite(moduleId);
  };

  const defaultTrigger = (
    <Button
      variant="ghost"
      size="icon"
      className="text-inherit hover:bg-white/10"
      aria-label={t('modules.switchModule')}
      title={t('modules.switchModule')}
      data-testid="module-switcher"
    >
      <Grip className="h-5 w-5" />
    </Button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger || defaultTrigger}
      </PopoverTrigger>
      {/* Cap to the space Radix measured between the trigger and the viewport
          edge; the module list inside keeps its own 400px `maxHeight` as the
          design ceiling and shrinks below it. Without this the picker runs off
          the bottom of a short screen and the last modules cannot be scrolled
          to, because the list IS the scroll container that is off-screen. */}
      <PopoverContent
        align={resolvedAlign}
        collisionPadding={8}
        className="flex max-h-[var(--radix-popover-content-available-height)] w-60 flex-col p-0"
      >
        <ModulePickerContent
          pickerModules={pickerModules}
          disabledPickerModules={disabledPickerModules}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          isCurrentModule={isCurrentModule}
          isModuleEnabledForUser={isModuleEnabledForUser}
          onModuleSelect={handleModuleSelect}
          onPageSelect={handlePageSelect}
          onHomeSelect={handleHomeSelect}
          isFavorite={isFavorite}
          onToggleFavorite={handleToggleFavorite}
          sortModules={sortModules}
          isOpen={open}
          pinnedModules={pinnedModules}
          dynamicItems={dynamicItems}
        />
      </PopoverContent>
    </Popover>
  );
}
